import React from 'react';
import { motion } from 'framer-motion';


import RelianceLogo from '@/assets/logos/reliance.png';
import TataAIGLogo from '@/assets/logos/tataaig.png';
import BajajLogo from '@/assets/logos/bajaj.png';
import MarshLogo from '@/assets/logos/marsh.png';

import Shield from '@/assets/icons/shield.png';
import File1 from '@/assets/icons/file.png';
import File2 from '@/assets/icons/file-2.png';
import Cart from '@/assets/icons/cart.png';

// Fallback for logos if you don't have them imported yet
const partners = [
  { name: 'Reliance General Insurance', src: RelianceLogo, width: 275 },
  { name: 'Tata AIG Insurance', src: TataAIGLogo, width: 156 },
  { name: 'Bajaj Allianz', src: BajajLogo, width: 216 },
  { name: 'Marsh', src: MarshLogo, width: 216 },
];

const cards = [
  {
    icon: Shield,
    // icon: <ShieldCheck className="tw-text-[#00d8ff]" size={28} />,
    text: 'Licensed insurance-backed protection plans',
  },
  {
    text: 'Claims validated by Protega, covered by partners',
    icon: File1,
    //   icon: <FileCheck className="tw-text-[#00d8ff]" size={28} />,
  },
  {
    icon: File2,
    // icon: <Scale className="tw-text-[#00d8ff]" size={28} />,
    text: 'Compliant with Indian insurance regulations',
  },
  {
    icon: Cart,
    // icon: <ShoppingCart className="tw-text-[#00d8ff]" size={28} />,
    text: 'Scalable across online & offline purchases',
  },
];

const fadeInVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
      delay: 0.2 // Small initial delay
    }
  },
};

const InsurancePartners: React.FC = () => {
  const [isHovered, setIsHovered] = React.useState(false);
  const containerRef = React.useRef<HTMLElement>(null);


  return (
    <motion.section
      ref={containerRef}
      className="tw-relative tw-bg-black tw-px-6 md:tw-px-12 tw-py-20 md:tw-py-32 tw-flex tw-flex-col tw-items-center tw-justify-center tw-overflow-hidden"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }} // Trigger when 15% visible
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background Blobs - Centered with Scroll Breathing */}
      <motion.div
        className="tw-absolute tw-inset-0 tw-pointer-events-none"
      >
        <motion.div
          className="tw-absolute tw-inset-0 tw-m-auto tw-w-[600px] tw-h-[600px] tw-bg-[#00d8ff]/10 tw-rounded-full tw-blur-[120px]"
          animate={{
            x: isHovered ? [0, -60, 0] : [0, -30, 0],
            y: isHovered ? [0, 40, 0] : [0, -20, 0],
            scale: isHovered ? 1.4 : 1.1,
          }}
          transition={{
            duration: isHovered ? 7 : 12,
            repeat: Infinity,
            repeatType: "reverse",
          }}
        />
      </motion.div>

      {/* Content Container */}
      <div className="tw-relative tw-z-10 tw-flex tw-flex-col tw-items-center tw-w-full">
        {/* Heading */}
        <motion.div
          className="tw-max-w-4xl tw-text-center tw-mb-12"
          variants={fadeInVariant}
        >
          <h2 className="tw-text-4xl md:tw-text-6xl lg:tw-text-7xl tw-font-bold tw-text-white tw-leading-tight tw-mb-2 lg:tw-mb-6">
            <span className="tw-text-[#00d8ff]">Insurance</span> that works <br className="tw-hidden md:tw-block" />
            behind the scenes
          </h2>
          <p className="tw-text-gray-300 tw-text-xs sm:tw-text-sm md:tw-text-lg sm:tw-max-w-xl tw-mx-auto tw-leading-relaxed">
            Protega partners with licensed insurance providers to power product protection—quietly, reliably, and at scale.
          </p>
        </motion.div>

        {/* Feature Grid */}
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-8 tw-w-full tw-max-w-5xl tw-mb-12">
          {cards.map((card, idx) => (
            <motion.div
              key={idx}
              variants={fadeInVariant}
              className="tw-bg-black/40 tw-backdrop-blur-sm tw-border tw-border-[#00d8ff]/10 tw-shadow-[0px_0px_20px_0px_#00C5FB80] tw-rounded-3xl tw-p-2 lg:tw-px-6 md:tw-py-4 tw-flex tw-items-center tw-gap-5 tw-transition-all tw-duration-300"
            >
              <div className="tw-flex-shrink-0 tw-h-14 tw-w-14 tw-flex tw-items-center tw-justify-center tw-rounded-xl">
                <img src={card.icon} alt={card.text} />
              </div>
              <p className="tw-text-white tw-font-semibold tw-text-base lg:tw-text-xl xl:tw-text-2xl tw-leading-snug">
                {card.text}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Bottom Text */}
        <motion.div
          className="tw-text-center tw-max-w-3xl tw-mb-16"
          variants={fadeInVariant}
        >
          <p className="tw-text-gray-200 tw-text-sm md:tw-text-base lg:tw-text-lg tw-leading-relaxed">
            <span className="tw-text-white tw-block tw-mb-2">Protection you can trust. Backed by insurance.</span>
            From checkout to claims, Protega ensures every protection plan is supported by trusted insurers—so you can scale with confidence.
          </p>
        </motion.div>

        {/* Partners Logos */}
        <motion.div
          className="tw-flex tw-flex-wrap tw-max-w-5xl tw-justify-center tw-items-center tw-gap-8 md:tw-gap-12 tw-brightness-110"
          variants={fadeInVariant}
        >
          {partners.map((partner, idx) => (
            <div key={idx} className="tw-transition-all tw-duration-300">
              <img
                src={partner.src}
                alt={partner.name}
                className="tw-object-contain tw-w-[calc(var(--logo-w)*0.6)] md:tw-w-[calc(var(--logo-w)*0.75)] lg:tw-w-[calc(var(--logo-w)*0.85)] xl:tw-w-[var(--logo-w)] tw-h-auto"
                style={{ "--logo-w": `${partner.width}px` } as React.CSSProperties}
              />
            </div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

export default InsurancePartners;